import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import {
  AttendanceIcon,
  ChevronLeftIcon,
  SettingsIcon,
  StaffIcon,
  VisitorIcon,
} from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { useAttendanceState } from '@/context/AttendanceContext';
import { useSessionState } from '@/context/SessionContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { formatInrCurrency, formatShortDate, formatShortDateTime, getInitials } from '@/lib/format';
import { tintStyle } from '@/lib/style';

export function StaffDetailPage() {
  const params = useParams<{ staffId?: string }>();
  const { sessionRole, isEnterprise, isReady: sessionReady } = useSessionState();
  const { isReady: staffReady, staffMembers } = useStaffDirectoryState();
  const { isReady: attendanceReady, sessions } = useAttendanceState();

  const staffId = typeof params.staffId === 'string' ? params.staffId : null;
  const staff = useMemo(() => {
    if (!staffId) {
      return null;
    }

    return staffMembers.find((member) => member.id === staffId) ?? null;
  }, [staffId, staffMembers]);

  const staffSessions = useMemo(() => {
    if (!staffId) {
      return [];
    }

    return sessions.filter((session) => session.staffId === staffId);
  }, [staffId, sessions]);

  const activeSession = staffSessions.find((session) => session.clockOutAt === null) ?? null;
  const completedStaffSessions = staffSessions.filter((session) => session.clockOutAt !== null);

  if (!sessionReady || !staffReady || !attendanceReady) {
    return <div className="section">Loading staff profile...</div>;
  }

  if (sessionRole === null) {
    return <Navigate to="/login" replace />;
  }

  if (!isEnterprise) {
    return <Navigate to="/attendance" replace />;
  }

  if (!staffId || !staff) {
    return (
      <div className="section page-grid">
        <div className="panel center-panel">
          <h1 className="panel-title">Staff profile not found</h1>
          <p className="panel-caption top-stack-sm">
            The requested staff record does not exist in the current directory.
          </p>
          <div className="button-row hero-actions center">
            <Link className="primary-button" to="/content-management/staff">
              <ChevronLeftIcon size={16} />
              Back to staff
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section page-grid staff-detail-page">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <StaffIcon size={14} />
            <strong>Staff</strong>
            <span>{activeSession ? 'On shift' : 'Profile view'}</span>
          </span>

          <h1 className="display-title">
            <span>{staff.name}</span>
            <span>{staff.role}</span>
          </h1>

          <div className="button-row hero-actions">
            <Link className="ghost-button" to="/content-management/staff">
              <ChevronLeftIcon size={16} />
              Back
            </Link>
            <div className="icon-action-row">
              <Link className="icon-button" to="/attendance" title="Attendance" aria-label="Open attendance">
                <AttendanceIcon size={18} />
              </Link>
              <Link className="icon-button" to="/visitor" title="Register visitor" aria-label="Register visitor">
                <VisitorIcon size={18} />
              </Link>
              <Link
                className="icon-button"
                to="/content-management/staff"
                title="Edit directory"
                aria-label="Edit directory record">
                <SettingsIcon size={18} />
              </Link>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          <StatMetric label="Bookings" value={String(staff.bookings)} hint="From performance" />
          <StatMetric label="Revenue" value={formatInrCurrency(staff.revenue)} hint="Team revenue" />
          <StatMetric label="Retention" value={`${staff.retention}%`} hint="Repeat rate" />
          <StatMetric label="Rating" value={staff.rating.toFixed(1)} hint="Average rating" />
        </div>
      </section>

      <section className="content-grid">
        <div className="panel">
          <div className="panel-title-row">
            <div>
              <h2 className="panel-title">Profile</h2>
              <p className="panel-caption">Contact and specialty</p>
            </div>
            <div className="badge soft">{staff.specialty}</div>
          </div>

          <div className="list-item centered">
            <div className="media-row lg">
              <div className="avatar xl tinted" style={tintStyle(staff.color)}>
                {getInitials(staff.name)}
              </div>
              <div>
                <strong>{staff.name}</strong>
                <span>{staff.email}</span>
                <p>{staff.phoneNumber}</p>
              </div>
            </div>
            <div className="item-meta">
              <span className="badge soft bottom-stack-sm">{staff.role}</span>
              <strong>{activeSession ? 'Active now' : 'Available'}</strong>
            </div>
          </div>
        </div>

        {completedStaffSessions.length > 0 ? (
          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Attendance</h2>
                <p className="panel-caption">Completed shift history</p>
              </div>
            </div>

            <div className="list">
              {completedStaffSessions.map((session) => (
                <div className="list-item" key={session.id}>
                  <div>
                    <strong>Completed shift</strong>
                    <span>{formatShortDate(session.clockInAt)}</span>
                    <p>
                      In {formatShortDateTime(session.clockInAt)}
                      {session.clockOutAt ? ` · Out ${formatShortDateTime(session.clockOutAt)}` : null}
                    </p>
                  </div>
                  <div className="item-meta">
                    <span className="badge soft bottom-stack-sm">Closed</span>
                    <strong>
                      {session.clockInLocation.latitude.toFixed(3)}, {session.clockInLocation.longitude.toFixed(3)}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
